import { Check, Plane, Clock, MapPin, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

const pricingCards = [
  {
    id: 'airport',
    icon: Plane,
    title: 'Airport Transfers',
    description: 'Seamless pickup and drop-off at Brussels Airport',
    basePrice: '€45',
    priceNote: 'Starting from',
    features: [
      'Flight tracking included',
      'Meet & greet service',
      '60 min free waiting time',
      'Luggage assistance',
      'Fixed prices - no surprises',
    ],
    routes: [
      { from: 'Brussels Airport', to: 'City Center', price: '€45' },
      { from: 'Brussels Airport', to: 'EU Quarter', price: '€50' },
      { from: 'Brussels Airport', to: 'Antwerp', price: '€120' },
      { from: 'Brussels Airport', to: 'Bruges', price: '€180' },
    ],
    popular: true,
  },
  {
    id: 'hourly',
    icon: Clock,
    title: 'Hourly Rental',
    description: 'Flexible chauffeur service for meetings and events',
    basePrice: '€65',
    priceNote: 'Per hour',
    features: [
      'Professional chauffeur',
      'Luxury vehicle of choice',
      'Multiple stops included',
      'Flexible scheduling',
      'Minimum 2 hours',
    ],
    packages: [
      { hours: '2 hours', price: '€130' },
      { hours: '4 hours', price: '€240' },
      { hours: '8 hours (full day)', price: '€450' },
    ],
    popular: false,
  },
  {
    id: 'daytrips',
    icon: MapPin,
    title: 'Day Trips',
    description: 'Explore Belgium and beyond in ultimate comfort',
    basePrice: '€350',
    priceNote: 'Starting from',
    features: [
      'All-inclusive pricing',
      'Professional tour guidance',
      'Customizable itinerary',
      'Refreshments included',
      'Photo stops on request',
    ],
    destinations: [
      { name: 'Bruges Day Trip', price: '€350' },
      { name: 'Ghent & Bruges', price: '€450' },
      { name: 'Amsterdam', price: '€550' },
      { name: 'Paris', price: '€750' },
    ],
    popular: false,
  },
];

const vehicleRates = [
  { category: 'Standard', perKm: '€2.00', baseFare: '€5', description: 'Mercedes E-Class or similar' },
  { category: 'Luxury', perKm: '€3.00', baseFare: '€8', description: 'Mercedes S-Class, BMW 7 Series' },
  { category: 'Van', perKm: '€2.50', baseFare: '€10', description: 'Mercedes V-Class (up to 7 passengers)' },
];

const PricingPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 bg-secondary">
        <div className="absolute inset-0 bg-gradient-to-b from-secondary-dark/50 to-secondary"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-white mb-6">
              Transparent Pricing
            </h1>
            <div className="w-24 h-1 bg-primary mx-auto mb-6"></div>
            <p className="text-xl text-gray-300">
              No hidden fees. No surprises. Just premium service at fair rates.
            </p>
          </div>
        </div>
      </section>

      {/* Service Rate Cards */}
      <section className="py-20 bg-neutral-cream">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-secondary mb-4">
              Choose Your Service
            </h2>
            <p className="text-gray-600">
              Select the service that best fits your needs
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {pricingCards.map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.id}
                  className={`relative bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
                    card.popular ? 'ring-2 ring-primary' : ''
                  }`}
                >
                  {card.popular && (
                    <div className="absolute top-0 right-0 bg-primary text-white px-4 py-1 text-sm font-medium rounded-bl-lg">
                      Most Popular
                    </div>
                  )}
                  
                  <div className="p-8">
                    <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
                      <Icon className="w-7 h-7 text-primary" />
                    </div>
                    
                    <h3 className="text-2xl font-serif font-bold text-secondary mb-2">
                      {card.title}
                    </h3>
                    <p className="text-gray-600 mb-6">{card.description}</p>
                    
                    <div className="mb-6">
                      <span className="text-4xl font-bold text-primary">{card.basePrice}</span>
                      <span className="text-gray-500 ml-2">{card.priceNote}</span>
                    </div>

                    <ul className="space-y-3 mb-8">
                      {card.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-3">
                          <Check className="w-5 h-5 text-primary flex-shrink-0" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Routes/Packages/Destinations */}
                    <div className="border-t border-gray-100 pt-6">
                      <h4 className="text-sm font-semibold text-secondary uppercase tracking-wider mb-4">
                        {card.routes ? 'Popular Routes' : card.packages ? 'Packages' : 'Destinations'}
                      </h4>
                      <div className="space-y-2">
                        {(card.routes || card.packages || card.destinations)?.map((item: any, idx: number) => (
                          <div key={idx} className="flex justify-between items-center text-sm">
                            <span className="text-gray-600">
                              {item.from ? `${item.from} → ${item.to}` : item.hours || item.name}
                            </span>
                            <span className="font-semibold text-secondary">{item.price}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <Link
                      to="/reservation"
                      className="block w-full mt-8 py-3 px-6 text-center bg-primary text-white font-medium rounded-lg hover:bg-primary-dark transition-colors"
                    >
                      Book Now
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Vehicle Rates Table */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-secondary mb-4">
              Vehicle Categories
            </h2>
            <p className="text-gray-600">
              Choose from our premium fleet based on your comfort preferences
            </p>
          </div>

          <div className="max-w-4xl mx-auto overflow-hidden rounded-xl shadow-lg">
            <table className="w-full">
              <thead className="bg-secondary text-white">
                <tr>
                  <th className="px-6 py-4 text-left font-serif">Category</th>
                  <th className="px-6 py-4 text-left font-serif">Vehicle</th>
                  <th className="px-6 py-4 text-center font-serif">Base Fare</th>
                  <th className="px-6 py-4 text-center font-serif">Per KM</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {vehicleRates.map((vehicle, idx) => (
                  <tr key={idx} className="bg-white hover:bg-neutral-cream transition-colors">
                    <td className="px-6 py-4">
                      <span className="font-semibold text-secondary">{vehicle.category}</span>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{vehicle.description}</td>
                    <td className="px-6 py-4 text-center font-medium text-secondary">{vehicle.baseFare}</td>
                    <td className="px-6 py-4 text-center font-medium text-primary">{vehicle.perKm}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-center text-gray-500 mt-6 text-sm">
            * All prices include VAT. Night surcharge (10pm-6am) +20%. Weekend surcharge +10%.
          </p>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 bg-neutral-cream">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 fill-primary text-primary" />
              ))}
            </div>
            <p className="text-xl text-gray-700 italic mb-4">
              "Transparent pricing and exceptional service. No hidden fees, just premium quality."
            </p>
            <p className="text-gray-500">— Over 10,000 satisfied customers</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-secondary">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-6">
            Ready to Experience Luxury?
          </h2>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
            Book your premium chauffeur service today and travel in style
          </p>
          <Link
            to="/reservation"
            className="inline-block px-8 py-4 bg-primary text-white font-medium rounded-lg hover:bg-primary-dark transition-colors"
          >
            Book Your Ride Now
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
